import { PageHeader } from "@bednbody/ui/components/page-header";
import { Empty } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Callout } from "@bednbody/ui/components/callout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@bednbody/ui/components/alert-dialog";
import { Button } from "@bednbody/ui/components/button";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { cn } from "@bednbody/ui/lib/utils";
import { CheckCircleIcon, WalletIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import {
  linesToCsv,
  useApprovePayrollRun,
  useMarkPayrollRunPaid,
  usePayrollLines,
  usePayrollRuns,
  type PayrollLine,
} from "./payroll.hooks";

const peso = (n: number) => `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-warning/15 text-warning",
  approved: "bg-info/15 text-info",
  paid: "bg-success/15 text-success",
};

const lineColumns: ColumnDef<PayrollLine, unknown>[] = [
  {
    id: "staff",
    accessorFn: (line) => line.staff?.full_name ?? "",
    header: "Staff",
    cell: ({ row }) => (
      <AdminTableIdentity
        title={row.original.staff?.full_name ?? "—"}
        subtitle={row.original.staff?.role ?? "Staff"}
        fallback={(row.original.staff?.full_name ?? "S").slice(0, 2).toUpperCase()}
      />
    ),
  },
  { id: "role", accessorFn: (line) => line.staff?.role ?? "", header: "Role" },
  { id: "sessions", accessorFn: (line) => line.sessions ?? 0, header: "Sessions" },
  {
    id: "commission",
    accessorFn: (line) => line.commission ?? 0,
    header: "Commission",
    cell: ({ row }) => peso(row.original.commission ?? 0),
  },
  {
    id: "base",
    accessorFn: (line) => line.base_pay ?? 0,
    header: "Base",
    cell: ({ row }) => peso(row.original.base_pay ?? 0),
  },
  {
    id: "bonus",
    accessorFn: (line) => line.bonus_pay ?? 0,
    header: "Bonus",
    cell: ({ row }) => peso(row.original.bonus_pay ?? 0),
  },
  {
    id: "total",
    accessorFn: (line) => line.total,
    header: "Total",
    cell: ({ row }) => (
      <span className="font-semibold tabular-nums">{peso(row.original.total)}</span>
    ),
  },
  {
    id: "actions",
    header: "",
    enableSorting: false,
    cell: ({ row }) => (
      <AdminTableRowMenu
        label={row.original.staff?.full_name ?? "payroll line"}
        actions={[
          {
            label: "Copy line ID",
            onSelect: () => void navigator.clipboard.writeText(row.original.id),
          },
        ]}
      />
    ),
  },
];

interface ConfirmState {
  kind: "approve" | "mark-paid";
  runId: string;
  title: string;
  description: string;
}

/**
 * Payroll review — Business Logic §11: the nightly job writes draft runs;
 * HR/Admin approve; paid is marked after real-world disbursement. Executive
 * gets a read-only view (route allows them in; the Edge Function's
 * withAuth(["hr","admin","superadmin"]) does not — so no buttons).
 */
export function PayrollPage() {
  const { claims } = useAuth();
  const canTransition =
    claims?.role === "hr" || claims?.role === "admin" || claims?.role === "superadmin";

  const { data: runs, isLoading: runsLoading, isError } = usePayrollRuns();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Default to the newest run once loaded.
  useEffect(() => {
    if (!selectedRunId && runs && runs.length > 0) setSelectedRunId(runs[0]!.id);
  }, [runs, selectedRunId]);

  const selectedRun = useMemo(
    () => (runs ?? []).find((r) => r.id === selectedRunId) ?? null,
    [runs, selectedRunId],
  );
  const { data: lines, isLoading: linesLoading } = usePayrollLines(selectedRunId);

  const approve = useApprovePayrollRun();
  const markPaid = useMarkPayrollRunPaid();
  const mutationError = approve.error ?? markPaid.error;
  const busy = approve.isPending || markPaid.isPending;

  const totals = useMemo(() => {
    const l = lines ?? [];
    return {
      commission: l.reduce((s, x) => s + (x.commission ?? 0), 0),
      basePay: l.reduce((s, x) => s + (x.base_pay ?? 0), 0),
      bonusPay: l.reduce((s, x) => s + (x.bonus_pay ?? 0), 0),
      total: l.reduce((s, x) => s + x.total, 0),
    };
  }, [lines]);

  const exportCsv = (matching: PayrollLine[]) => {
    if (!selectedRun) return;
    const blob = new Blob([linesToCsv(selectedRun, matching)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll-${selectedRun.period_start}-${selectedRun.period_end}.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const runConfirmed = () => {
    if (!confirm) return;
    const { kind, runId } = confirm;
    setConfirm(null);
    if (kind === "approve") approve.mutate(runId);
    else markPaid.mutate(runId);
  };

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        description="Nightly runs land as drafts; HR/Admin approve, then mark paid after disbursement. Therapist commission is on full promo price; rider Base + Bonus come from each night's settled Rider Session earnings (OQ-71)."
        title="Payroll"
      />

      {isError && (
        <Callout tone="danger" role="alert">
          Couldn't load payroll runs — check your connection and try again.
        </Callout>
      )}
      {mutationError && (
        <Callout tone="danger" role="alert">
          {(mutationError as Error).message}
        </Callout>
      )}

      {runsLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-14 motion-reduce:animate-none" />
          <Skeleton className="h-64 motion-reduce:animate-none" />
        </div>
      ) : (runs ?? []).length === 0 ? (
        <Empty>
          <p className="text-sm font-medium text-foreground">No payroll runs yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            The nightly job (pg_cron) writes the first draft run after a day with completed
            sessions.
          </p>
        </Empty>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[280px_1fr]">
          {/* Run list */}
          <div className="space-y-2">
            {(runs ?? []).map((run) => (
              <button
                key={run.id}
                type="button"
                onClick={() => setSelectedRunId(run.id)}
                className={cn(
                  "w-full rounded-xl border px-4 py-3 text-left transition-colors",
                  run.id === selectedRunId
                    ? "border-primary bg-primary/5"
                    : "border-border bg-card hover:bg-background",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium tabular-nums text-foreground">
                    {run.period_start} → {run.period_end}
                  </p>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 type-body-micro font-medium capitalize",
                      STATUS_STYLES[run.status ?? ""] ?? "bg-border text-muted-foreground",
                    )}
                  >
                    {run.status}
                  </span>
                </div>
                {run.approver?.full_name ? (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    approved by {run.approver.full_name}
                  </p>
                ) : null}
              </button>
            ))}
          </div>

          {/* Selected run detail */}
          <div className="space-y-4">
            {selectedRun && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <WalletIcon size={18} className="text-primary" />
                  <p className="text-sm font-semibold text-foreground">
                    {selectedRun.period_start} → {selectedRun.period_end}
                  </p>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 type-body-micro font-medium capitalize",
                      STATUS_STYLES[selectedRun.status ?? ""] ?? "bg-border text-muted-foreground",
                    )}
                  >
                    {selectedRun.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {canTransition && selectedRun.status === "draft" && (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        setConfirm({
                          kind: "approve",
                          runId: selectedRun.id,
                          title: "Approve this payroll run?",
                          description: `${lines?.length ?? 0} lines totalling ${peso(totals.total)} for ${selectedRun.period_start} → ${selectedRun.period_end}. Approval authorizes pay (Business Logic §11) and is audit-logged.`,
                        })
                      }
                    >
                      <CheckCircleIcon size={14} /> Approve
                    </Button>
                  )}
                  {canTransition && selectedRun.status === "approved" && (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        setConfirm({
                          kind: "mark-paid",
                          runId: selectedRun.id,
                          title: "Mark this run as paid?",
                          description: `Confirms ${peso(totals.total)} was actually disbursed for ${selectedRun.period_start} → ${selectedRun.period_end}. This closes the run.`,
                        })
                      }
                    >
                      Mark paid
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {(
                [
                  ["Commission", totals.commission],
                  ["Base pay", totals.basePay],
                  ["Bonus pay", totals.bonusPay],
                  ["Run total", totals.total],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-border bg-card p-3 shadow-xs">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-lg font-semibold tabular-nums text-primary-dark dark:text-primary-light">
                    {linesLoading ? "…" : peso(value)}
                  </p>
                </div>
              ))}
            </div>

            <AdminDataTable
              presentation="standalone"
              data={lines ?? []}
              columns={lineColumns}
              getRowId={(line) => line.id}
              searchText={(line) => [line.staff?.full_name, line.staff?.role].join(" ")}
              searchPlaceholder="Search staff or role…"
              facets={[
                {
                  id: "role",
                  label: "Role",
                  options: [
                    { value: "therapist", label: "Therapists" },
                    { value: "rider", label: "Riders" },
                  ],
                  getValue: (line) => line.staff?.role ?? "",
                },
              ]}
              initialSorting={[{ id: "staff", desc: false }]}
              loading={linesLoading}
              emptyTitle="No lines in this run"
              onExport={exportCsv}
            />
          </div>
        </div>
      )}

      {/* Confirm dialog — pay authorization is one-way */}
      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
